import { AppState, Constraint, NoSolutionError, SecretSantaResult } from './types.ts';
import { SeededRandom } from "./prng.ts";

/**
 * Generates Secret Santa assignments using backtracking to find a Hamiltonian cycle
 * with the given constraints.
 *
 * @param state The application state with names, constraints, and seed
 * @returns Array where result[i] = j means person i gives to person j
 * @throws NoSolutionError if no valid solution exists
 */
export function generateSecretSanta(state: AppState): SecretSantaResult {
  const n = state.names.length;

  if (n === 0) {
    return [];
  }

  if (n === 1) {
    throw new NoSolutionError("Cannot create Secret Santa with only one person");
  }

  // Validate MUST constraints don't create conflicts
  validateMustConstraints(state);

  // Initialize result array (-1 means unassigned)
  const result: number[] = Array(n).fill(-1);

  // Track which people have been assigned as receivers
  const assigned = new Set<number>();

  // Create seeded random for deterministic shuffling
  const rng = new SeededRandom(state.seed);

  // Build priority order: people with MUST constraints go first
  const order = buildProcessingOrder(state, rng);

  // Try to solve using backtracking
  if (backtrack(state, result, assigned, order, 0)) {
    return result;
  }

  // If we failed, try to give a helpful error message
  const helpfulError = diagnoseFailure(state);
  throw new NoSolutionError(helpfulError);
}

/**
 * Diagnoses why the algorithm failed and returns a helpful error message
 */
function diagnoseFailure(state: AppState): string {
  const n = state.names.length;

  // Check if anyone has no valid recipients
  for (let i = 0; i < n; i++) {
    let validRecipients = 0;
    for (let j = 0; j < n; j++) {
      if (i !== j && state.constraints[i][j] !== Constraint.CANNOT) {
        validRecipients++;
      }
    }
    if (validRecipients === 0) {
      return `${state.names[i]} has no valid gift recipients due to constraints`;
    }
  }

  // Check if anyone can't receive from anyone
  for (let j = 0; j < n; j++) {
    let validGivers = 0;
    for (let i = 0; i < n; i++) {
      if (i !== j && state.constraints[i][j] !== Constraint.CANNOT) {
        validGivers++;
      }
    }
    if (validGivers === 0) {
      return `${state.names[j]} cannot receive a gift from anyone due to constraints`;
    }
  }

  // Check for isolated MUST chains that can't form a cycle
  const mustReceivers = new Map<number, number>();
  for (let i = 0; i < n; i++) {
    const mustIndex = state.constraints[i].findIndex((c) => c === Constraint.MUST);
    if (mustIndex !== -1) {
      mustReceivers.set(i, mustIndex);
    }
  }

  if (mustReceivers.size > 0) {
    // Check if MUST constraints form valid chains
    const visited = new Set<number>();
    for (const [giver, receiver] of mustReceivers) {
      if (!visited.has(giver)) {
        const chain: number[] = [];
        let current = giver;

        // Follow the MUST chain
        while (mustReceivers.has(current) && !visited.has(current)) {
          chain.push(current);
          visited.add(current);
          current = mustReceivers.get(current)!;
        }

        // Check if the chain ends in a dead end
        if (mustReceivers.has(current) && visited.has(current) && current !== giver) {
          return `MUST constraints create conflicting chains that cannot form a single cycle`;
        }
      }
    }
  }

  return "No valid Secret Santa solution exists with given constraints. Try relaxing some CANNOT or MUST constraints.";
}

/**
 * Validates that MUST constraints don't create impossible situations
 */
function validateMustConstraints(state: AppState): void {
  const n = state.names.length;

  // Check each person has at most one MUST outgoing
  for (let i = 0; i < n; i++) {
    const mustCount = state.constraints[i].filter((c) => c === Constraint.MUST).length;
    if (mustCount > 1) {
      throw new NoSolutionError(
        `${state.names[i]} has multiple MUST constraints, which is impossible`,
      );
    }
  }

  // Check each person has at most one MUST incoming
  for (let j = 0; j < n; j++) {
    let mustCount = 0;
    for (let i = 0; i < n; i++) {
      if (state.constraints[i][j] === Constraint.MUST) {
        mustCount++;
      }
    }
    if (mustCount > 1) {
      throw new NoSolutionError(
        `${state.names[j]} has multiple people who MUST give to them, which is impossible`,
      );
    }
  }
}

/**
 * Builds the order in which to process people
 * People with MUST constraints are prioritized
 */
function buildProcessingOrder(state: AppState, rng: SeededRandom): number[] {
  const n = state.names.length;
  const mustPeople: number[] = [];
  const otherPeople: number[] = [];

  for (let i = 0; i < n; i++) {
    const hasMust = state.constraints[i].some((c) => c === Constraint.MUST);
    if (hasMust) {
      mustPeople.push(i);
    } else {
      otherPeople.push(i);
    }
  }

  // Shuffle each group for randomization
  return [...rng.shuffle(mustPeople), ...rng.shuffle(otherPeople)];
}

/**
 * Backtracking algorithm to find valid Hamiltonian cycle
 */
function backtrack(
  state: AppState,
  result: number[],
  assigned: Set<number>,
  order: number[],
  depth: number,
): boolean {
  const n = state.names.length;

  // Base case: all people have been assigned
  if (depth === n) {
    // Verify it forms a single Hamiltonian cycle
    // We don't care about the starting point - just verify everyone is in one cycle
    const visited = new Set<number>();
    let current = 0;

    for (let i = 0; i < n; i++) {
      if (visited.has(current)) {
        return false; // Found a smaller cycle
      }
      visited.add(current);
      current = result[current];
    }

    // Check we visited everyone and ended back at the start
    return visited.size === n && current === 0;
  }

  const giver = order[depth];

  // Check if this person has a MUST constraint
  const mustIndex = state.constraints[giver].findIndex((c) => c === Constraint.MUST);
  if (mustIndex !== -1) {
    // This person MUST give to a specific person
    if (assigned.has(mustIndex)) {
      // The required receiver is already taken
      return false;
    }

    result[giver] = mustIndex;
    assigned.add(mustIndex);

    if (backtrack(state, result, assigned, order, depth + 1)) {
      return true;
    }

    // Backtrack
    result[giver] = -1;
    assigned.delete(mustIndex);
    return false;
  }

  // Try all possible receivers in random order
  const rng = new SeededRandom(state.seed + giver);
  const possibleReceivers = rng.shuffle(
    Array.from({ length: n }, (_, i) => i).filter(
      (receiver) => !assigned.has(receiver) && canGiveTo(state, giver, receiver),
    ),
  );

  for (const receiver of possibleReceivers) {
    result[giver] = receiver;
    assigned.add(receiver);

    if (backtrack(state, result, assigned, order, depth + 1)) {
      return true;
    }

    // Backtrack
    result[giver] = -1;
    assigned.delete(receiver);
  }

  return false;
}

/**
 * Checks if giver can give to receiver based on constraints
 */
function canGiveTo(state: AppState, giver: number, receiver: number): boolean {
  const constraint = state.constraints[giver][receiver];
  return constraint !== Constraint.CANNOT;
}

/**
 * Validates that a result forms a proper Hamiltonian cycle
 */
export function validateResult(state: AppState, result: SecretSantaResult): boolean {
  const n = state.names.length;

  if (result.length !== n) {
    return false;
  }

  // Check all receivers are unique and valid
  const receivers = new Set(result);
  if (receivers.size !== n) {
    return false;
  }

  for (let i = 0; i < n; i++) {
    const receiver = result[i];
    if (receiver < 0 || receiver >= n) {
      return false;
    }

    // Check constraint
    if (!canGiveTo(state, i, receiver)) {
      return false;
    }
  }

  // Check it forms a cycle
  const visited = new Set<number>();
  let current = 0;
  for (let i = 0; i < n; i++) {
    if (visited.has(current)) {
      return false; // Found a smaller cycle
    }
    visited.add(current);
    current = result[current];
  }

  return visited.size === n && current === 0;
}

/**
 * Formats result as a human-readable chain
 */
export function formatResult(state: AppState, result: SecretSantaResult): string {
  if (result.length === 0) {
    return "No participants";
  }

  const n = result.length;

  // Pick a random starting point based on the seed
  const rng = new SeededRandom(state.seed);
  const startIndex = rng.nextInt(0, n);

  const chain: string[] = [];
  let current = startIndex;

  for (let i = 0; i < n; i++) {
    chain.push(state.names[current]);
    current = result[current];
  }

  // Add the starting person at the end to complete the cycle
  chain.push(state.names[startIndex]);

  return chain.join(" → ");
}
